import Vue from 'vue';
import axios from 'axios';
import to from 'await-to-js';
import { get } from 'lodash';

// Helper function to clean layout data by removing null/undefined values
const cleanLayoutData = layoutData => {
  const cleaned = { ...layoutData };

  // Clean columnState by removing null/undefined values
  // Save hidden columns to correctly apply column ordering:
  // preventing error: Applying column order broke a group where columns should be married together. Applying new order has been discarded.
  if (cleaned.columnState && Array.isArray(cleaned.columnState)) {
    cleaned.columnState = cleaned.columnState.map(column => {
      const cleanColumn = { colId: column.colId }; // Always keep colId

      // Add non-null values
      Object.keys(column).forEach(key => {
        if (column[key] != null) {
          cleanColumn[key] = column[key];
        }
      });

      return cleanColumn;
    });
  }

  return cleaned;
};

const getInitialState = () => {
  return {
    loading: false,
    saving: false,
    deleting: false,
    layouts: [],
    activeLayout: {},
    error: null,
  };
};

const store = {
  namespaced: true,

  state: getInitialState(),

  getters: {
    getLayouts(state) {
      return [...state.layouts].sort((a, b) => a.name.localeCompare(b.name));
    },

    // Get active layout for a specific grid type
    getActiveLayout: state => gridType => {
      return state.activeLayout[gridType] || null;
    },

    // Check if any operation is in progress
    isLoading: state => state.loading || state.saving || state.deleting,
  },

  mutations: {
    resetState(state) {
      Object.assign(state, getInitialState());
    },

    setLoading(state, loading) {
      state.loading = loading;
    },

    setSaving(state, saving) {
      state.saving = saving;
    },

    setDeleting(state, deleting) {
      state.deleting = deleting;
    },

    setLayouts(state, layouts) {
      state.layouts = layouts;
    },

    addLayout(state, layout) {
      state.layouts = [...state.layouts, layout];
    },

    removeLayout(state, layoutId) {
      state.layouts = state.layouts.filter(layout => layout._id !== layoutId);
    },

    updateLayoutInList(state, updatedLayout) {
      const index = state.layouts.findIndex(layout => layout._id === updatedLayout._id);
      if (index !== -1) {
        Vue.set(state.layouts, index, updatedLayout);
      }
    },

    setActiveLayout(state, { gridType, layout }) {
      state.activeLayout = {
        ...state.activeLayout,
        [gridType]: layout,
      };
    },

    setError(state, error) {
      state.error = error;
    },

    clearError(state) {
      state.error = null;
    },
  },

  actions: {
    resetState({ commit }) {
      commit('resetState');
    },

    // Create a new layout
    async createLayout({ commit }, layoutData) {
      commit('setSaving', true);
      commit('clearError');

      try {
        // Clean the layout data to remove null values before sending
        const cleanedLayoutData = cleanLayoutData(layoutData);
        const [err, response] = await to(axios.post('/api/grid-layouts', cleanedLayoutData));

        if (err) {
          commit('setError', get(err, 'response.data.message', 'Failed to create layout'));
          throw err;
        }

        commit('addLayout', response.data);
        return response.data;
      } finally {
        commit('setSaving', false);
      }
    },

    // Update an existing layout
    async updateLayout({ commit, state }, { layoutId, layoutData, gridType }) {
      commit('setSaving', true);
      commit('clearError');

      try {
        const cleanedLayoutData = cleanLayoutData(layoutData);
        const [err, response] = await to(
          axios.patch(`/api/grid-layouts/${layoutId}`, cleanedLayoutData)
        );

        if (err) {
          commit('setError', get(err, 'response.data.message', 'Failed to update layout'));
          throw err;
        }

        // Update the layout in the list
        commit('updateLayoutInList', response.data);

        // Update active layout if it's the one being updated
        const activeLayout = state.activeLayout[gridType];
        if (activeLayout && activeLayout._id === layoutId) {
          commit('setActiveLayout', { gridType, layout: response.data });
        }

        return response.data;
      } finally {
        commit('setSaving', false);
      }
    },

    async deleteLayout({ commit }, layoutId) {
      commit('setDeleting', true);
      commit('clearError');

      try {
        const [err] = await to(axios.delete(`/api/grid-layouts/${layoutId}`));

        if (err) {
          commit('setError', get(err, 'response.data.message', 'Failed to delete layout'));
          throw err;
        }

        commit('removeLayout', layoutId);
        return true;
      } finally {
        commit('setDeleting', false);
      }
    },

    clearActiveLayout({ commit }, gridType) {
      commit('setActiveLayout', { gridType, layout: null });
    },

    // fetch layouts for specific grid type as well as user preferred layout
    // set active layout to user preferred layout if it exists
    async initLayoutsAndSetActive({ commit, rootGetters }, { gridType }) {
      commit('setLoading', true);
      commit('clearError');
      try {
        // Start both requests in parallel
        const [layoutsRes, prefRes] = await Promise.all([
          axios.get(`/api/grid-layouts/grid-type/${gridType}`),
          axios.get('/api/user-preferences/grid-layout', {
            params: {
              associatedTable: gridType,
              workpackageType: rootGetters['workpackages/getSelectedWorkpackage'].type,
            },
          }),
        ]);

        // Set layouts in state
        commit('setLayouts', layoutsRes.data);

        // Find the preferred layout in the fetched layouts
        let activeLayout = null;
        if (prefRes.data && prefRes.data.layoutId) {
          activeLayout = layoutsRes.data.find(l => l._id === prefRes.data.layoutId) || null;
        }
        commit('setActiveLayout', { gridType, layout: activeLayout });

        return { layouts: layoutsRes.data, activeLayout };
      } catch (err) {
        commit('setError', get(err, 'response.data.message', 'Failed to initialize layouts'));
        throw err;
      } finally {
        commit('setLoading', false);
      }
    },

    setActiveLayout({ commit }, { layout, gridType }) {
      commit('setActiveLayout', { gridType, layout });
    },

    async saveLayoutPreference({ rootGetters }, { layout, gridType }) {
      const [err] = await to(
        axios.post('/api/user-preferences/grid-layout', {
          associatedTable: gridType,
          workpackageType: rootGetters['workpackages/getSelectedWorkpackage'].type,
          layoutId: layout._id,
        })
      );
      if (err) {
        console.error('Failed to save active layout preference', err);
        throw err;
      }
    },

    // set active layout and save to backend as user preference
    async setActiveLayoutAndSave({ dispatch }, { layout, gridType }) {
      dispatch('setActiveLayout', { layout, gridType });
      await dispatch('saveLayoutPreference', { layout, gridType });
    },

    // create new layout and set it as active layout and save to backend as user preference
    async createAndSetActiveLayout({ dispatch }, { layoutData, gridType }) {
      const newLayout = await dispatch('createLayout', layoutData);
      await dispatch('setActiveLayoutAndSave', { layout: newLayout, gridType });
      return newLayout;
    },

    // delete layout and clear active layout if it is the active layout
    async deleteAndClearActiveLayout({ dispatch, state }, { layoutId, gridType }) {
      await dispatch('deleteLayout', layoutId);
      const activeLayoutForGridType = state.activeLayout[gridType];
      if (activeLayoutForGridType && activeLayoutForGridType._id === layoutId) {
        await dispatch('clearActiveLayout', gridType);
      }
    },
  },
};

export default store;
